import { AlarmClock } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { Task } from '@/types/task';
import { getTaskStatus, formatCompactDueDate, getDueMetaColorVar, formatSnoozeLabel } from '@/utils/task-display-utils';
import { QuickCompleteCheckbox } from '@/components/common/QuickCompleteCheckbox';
import { OperationalRow } from '@/components/common/OperationalRow';

interface TaskRowProps {
  task: Task;
  seasonId: string;
  predecessorTitles?: string[];
  /** Legacy toggle for TaskListPage — used when onQuickComplete is not provided */
  onToggleComplete: (task: Task) => void;
  /** Dashboard quick-complete: optimistic async handler; takes priority over onToggleComplete */
  onQuickComplete?: (id: string) => Promise<void>;
  /** Controlled inline error message (keyed by task id in parent); auto-cleared by parent */
  errorMessage?: string;
  /**
   * Preview mode — used on TemplateDetailPage to show tasks without interactive UI.
   * When true: outer element is a plain <div> (not a <Link>), checkbox is hidden.
   */
  preview?: boolean;
  /** Subtext to display in preview mode (e.g. timing description) */
  previewSubtext?: string;
  /**
   * When false, completed task titles do NOT render with line-through.
   * Defaults to true (existing behaviour). Set to false on the Prep task list where
   * the stripe/glyph already encodes done state and strikethrough is visual noise.
   */
  showStrikethrough?: boolean;
}

export function TaskRow({
  task,
  seasonId,
  predecessorTitles = [],
  onToggleComplete,
  onQuickComplete,
  errorMessage,
  preview = false,
  previewSubtext,
  showStrikethrough = true,
}: TaskRowProps) {
  const todayDate = new Date();
  const today = todayDate.toISOString().slice(0, 10);
  const status = getTaskStatus(task, today);
  const isBlocked = status === 'blocked';
  const isSnoozed = status === 'snoozed';

  // When onQuickComplete is provided (dashboard path), use it.
  // When absent (TaskListPage path), wrap onToggleComplete in async for QuickCompleteCheckbox.
  async function handleCheckboxComplete(id: string): Promise<void> {
    if (onQuickComplete) {
      await onQuickComplete(id);
    } else {
      onToggleComplete(task);
    }
  }

  const secondaryLine = !preview && (
    <>
      {isBlocked && (
        <div className="flex items-center gap-1 mt-0.5" data-testid="blocked-indicator">
          <span className="text-body-xs text-[var(--color-text-muted)] truncate">
            {predecessorTitles.length > 0
              ? `Blocked by: ${predecessorTitles.join(', ')}`
              : 'Blocked by incomplete dependencies'}
          </span>
        </div>
      )}

      {isSnoozed && task.snoozedUntil && (
        <div className="flex items-center gap-1 mt-0.5" data-testid="snoozed-indicator">
          <AlarmClock size={11} className="text-[#5a7a8a] flex-shrink-0" />
          <span className="text-body-xs text-[#5a7a8a] truncate">
            {formatSnoozeLabel(task.snoozedUntil)}
          </span>
        </div>
      )}

      {!isBlocked && !isSnoozed && task.selectedAction && (
        <p className="text-body-xs text-[var(--color-text-muted)] mt-0.5 truncate">
          {task.selectedAction}
        </p>
      )}
    </>
  );

  if (preview) {
    return (
      <OperationalRow
        status={status}
        data-testid="task-row"
        preview={true}
      >
        <div className="flex-1 min-w-0 flex items-start gap-2 px-3 py-3.5">
          <div className="flex-1 min-w-0">
            <p
              className={`truncate ${
                task.completed
                  ? `text-sm font-normal ${showStrikethrough ? 'line-through ' : ''}text-[var(--color-text-muted)]`
                  : 'text-[15px] font-medium text-[var(--color-text-primary)]'
              }`}
              data-state={task.completed ? 'completed' : 'active'}
              data-strikethrough={showStrikethrough && task.completed ? 'true' : 'false'}
            >
              {task.title}
            </p>

            {previewSubtext && (
              <span
                className="text-body-xs text-[var(--color-text-muted)] mt-0.5 block"
                data-testid="task-preview-subtext"
              >
                {previewSubtext}
              </span>
            )}

            {errorMessage && (
              <p
                className="text-body-xs text-[var(--color-accent-hover)] mt-0.5"
                data-testid="task-row-error"
              >
                {errorMessage}
              </p>
            )}
          </div>
        </div>
      </OperationalRow>
    );
  }

  return (
    <OperationalRow
      status={status}
      data-testid="task-row"
      className="hover:bg-[var(--color-elevated)]/50 group"
    >
      {/* Quick-complete checkbox — sibling of Link, NOT nested inside it */}
      <div data-testid="task-checkbox">
        <QuickCompleteCheckbox
          task={task}
          onComplete={handleCheckboxComplete}
          size="sm"
        />
      </div>

      {/* Row content — navigates to task detail */}
      <Link
        to={`/prep/tasks/${task.id}?season=${seasonId}`}
        className="flex-1 min-w-0 flex items-start gap-2 px-3 py-3.5"
        data-testid="task-row-link"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p
              className={`truncate ${
                task.completed
                  ? `text-sm font-normal ${showStrikethrough ? 'line-through ' : ''}text-[var(--color-text-muted)]`
                  : 'text-[15px] font-medium text-[var(--color-text-primary)]'
              }`}
              data-state={task.completed ? 'completed' : 'active'}
              data-strikethrough={showStrikethrough && task.completed ? 'true' : 'false'}
            >
              {task.title}
            </p>
            <span
              className="text-body-xs flex-shrink-0"
              style={{ color: getDueMetaColorVar(status) }}
            >
              {formatCompactDueDate(task, todayDate)}
            </span>
          </div>

          {/* Inline error message (controlled by parent) */}
          {errorMessage && (
            <p
              className="text-body-xs text-[var(--color-accent-hover)] mt-0.5"
              data-testid="task-row-error"
            >
              {errorMessage}
            </p>
          )}

          {secondaryLine}
        </div>
      </Link>
    </OperationalRow>
  );
}
